import { type ConsentMode, type ConsentState, type GoogleConsentSettings } from '../../typings'
import { BaseCMP } from './base-cmp'

type PartialGoogleConsent = Partial<Record<keyof GoogleConsentSettings, ConsentMode>>

/** Keys of a `gtag('consent', …)` payload that are not consent types. */
const NON_CONSENT_KEYS = new Set(['region', 'wait_for_update'])

/**
 * Reads consent from Google Consent Mode instead of from a CMP we installed
 * ourselves.
 *
 * Every Consent Mode v2 CMP — Cookiebot, CookieScript, Cookie Information,
 * OneTrust, Usercentrics, Axeptio — ends up calling
 * `gtag('consent', 'default' | 'update', {...})`, which is just a push of an
 * `arguments` object onto a data layer. Observing that is CMP-agnostic, and it
 * is the only way to hear a CMP that is loaded from *inside* the merchant's GTM
 * container, where we have no script of our own to hook.
 *
 * No script is injected: this adapter is pure observation.
 */
export class ConsentModeCMP extends BaseCMP {
  /** Accumulated Google state — an `update` may carry only some of the keys. */
  private googleState: PartialGoogleConsent = {}
  private readonly detachers: (() => void)[] = []
  private hasSignal = false

  initialize(): void {
    if (this.initialized) {
      return
    }

    this.setupEventListeners()
    this.initialized = true
    this.log('Observing Google Consent Mode', this.config.dataLayerNames)
  }

  protected setupEventListeners(): void {
    const names = this.config.dataLayerNames?.length ? this.config.dataLayerNames : ['dataLayer']

    names.forEach((name) => this.observeDataLayer(name))
  }

  /**
   * Attaches to one data layer so that consent commands are seen whether they
   * were pushed before us or after.
   */
  private observeDataLayer(name: string): void {
    if (typeof window === 'undefined') {
      return
    }

    const scope = window as unknown as Record<string, unknown[]>
    const dataLayer = (scope[name] = scope[name] || [])

    // Anything already queued — a CMP that restored a stored choice before we
    // mounted would otherwise be invisible until the user touches the banner.
    dataLayer.forEach((entry) => this.inspect(entry))

    let underlying: (...args: unknown[]) => number = Array.prototype.push.bind(dataLayer) as (
      ...args: unknown[]
    ) => number
    let depth = 0
    const inspect = (entry: unknown) => this.inspect(entry)

    const observer = (...args: unknown[]): number => {
      args.forEach(inspect)

      // gtm.js replaces `push` with its own implementation. That lands on the
      // setter below, so we keep delegating to it. If some implementation ever
      // calls back into the previous `push` (i.e. this observer), delegating
      // again would recurse forever — beyond the first level, append natively.
      if (depth > 0) {
        return Array.prototype.push.apply(dataLayer, args)
      }

      depth += 1
      try {
        return underlying.apply(dataLayer, args)
      } finally {
        depth -= 1
      }
    }

    try {
      Object.defineProperty(dataLayer, 'push', {
        configurable: true,
        enumerable: false,
        get: () => observer,
        set: (fn: (...args: unknown[]) => number) => {
          underlying = fn
        }
      })
    } catch (error) {
      // A frozen/sealed data layer: replay still worked, live updates won't.
      this.logError(`Could not observe ${name}`, error)
      return
    }

    this.detachers.push(() => {
      try {
        // Hand `push` back as a plain property holding whatever GTM installed —
        // a bare `delete` would drop GTM's own implementation and break it.
        Object.defineProperty(dataLayer, 'push', {
          configurable: true,
          enumerable: false,
          writable: true,
          value: underlying
        })
      } catch (error) {
        this.logError(`Could not detach from ${name}`, error)
      }
    })
  }

  /** Recognises `gtag('consent', 'default' | 'update', {...})` in any form. */
  private inspect(entry: unknown): void {
    if (!entry || typeof entry !== 'object') {
      return
    }

    // The gtag shim pushes an `arguments` object, not an array — both are
    // indexable, so read positionally rather than testing for Array.
    const command = entry as Record<number, unknown>

    if (command[0] !== 'consent') {
      return
    }

    const verb = command[1]
    if (verb !== 'update' && verb !== 'default') {
      return
    }

    const settings = command[2]
    if (!settings || typeof settings !== 'object') {
      return
    }

    const payload = settings as Record<string, unknown>

    // Region-scoped defaults describe somewhere else; applying them would
    // overwrite the global default with a rule that does not apply here.
    if (verb === 'default' && Array.isArray(payload.region)) {
      return
    }

    this.merge(payload)
  }

  private merge(payload: Record<string, unknown>): void {
    let changed = false

    Object.entries(payload).forEach(([key, value]) => {
      if (NON_CONSENT_KEYS.has(key)) {
        return
      }
      if (value !== 'granted' && value !== 'denied') {
        return
      }
      const typedKey = key as keyof GoogleConsentSettings
      if (this.googleState[typedKey] !== value) {
        this.googleState[typedKey] = value
        changed = true
      }
    })

    if (!changed) {
      return
    }

    this.hasSignal = true
    this.handleConsentUpdate()
  }

  protected handleConsentUpdate(): void {
    const state = this.getConsentState()
    if (state && this.consentUpdateCallback) {
      this.consentUpdateCallback(state)
    }
  }

  getConsentState(): ConsentState | null {
    if (!this.hasSignal) {
      return null
    }

    const granted = (key: keyof GoogleConsentSettings) => this.googleState[key] === 'granted'

    return {
      analytics: granted('analytics_storage'),
      // Either signal means ad measurement is allowed; CMPs differ on which
      // they send first, and a user who granted marketing always gets both.
      marketing: granted('ad_storage') || granted('ad_user_data'),
      preferences: granted('functionality_storage'),
      client_timestamp: Date.now()
    }
  }

  destroy(): void {
    this.detachers.forEach((detach) => detach())
    this.detachers.length = 0
    this.googleState = {}
    this.hasSignal = false
    this.initialized = false
    this.consentUpdateCallback = undefined

    this.log('Destroyed Consent Mode CMP')
  }
}
